import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, ShoppingCart, Heart, User, LogOut, Plus, Menu, X, MapPin, Star, Package, Gift, Filter,
  Upload, Check, AlertCircle, Trash2, Eye, HelpCircle, ChevronRight, FileText, Shield, Mail,
  MapPinned, RefreshCw, BookOpen, ArrowLeftRight, Lock, Unlock, Users, Calendar, Award, Sparkles,
  Home, History, Bookmark, Settings, Info, ChevronDown, AlertTriangle, Truck, Download
} from "lucide-react";
import { axios, API, BACKEND_URL, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import {
  PriceDisplay, StatusBadge, TransactionTypeBadge, AgeBadge, KidTip, TierBadge
} from "../components/Badges";
import { TrustScoreBadge } from "../components/TrustScoreBadge";
import { ParentalGate } from "../components/ParentalGate";
import { ShippingCheckoutModal } from "../components/ShippingCheckoutModal";
import { ShippingLabelControls } from "../components/ShippingLabelControls";
import { ToyCycleLogo } from "../components/Logo";


const getButtonLabel = (loading, isLogin) => {
  if (loading) return "Loading...";
  return isLogin ? "Sign In" : "Create Account";
};

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", location: "", date_of_birth: "", is_parent: false, parental_pin: "", parent_email: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Referral hand-off — when the user arrives via /auth?ref=CODE, remember the
  // code so we can auto-redeem right after registration. We default to the
  // signup tab in that case so the friction is zero.
  const referralCode = (searchParams.get("ref") || "").trim().toUpperCase();
  useEffect(() => {
    if (referralCode) setIsLogin(false);
  }, [referralCode]);

  // Fire an anonymous click-tracking ping so the inviter sees their funnel
  // stats. Unknown codes are silently ignored server-side.
  useEffect(() => {
    if (!referralCode) return;
    axios.post(`${API}/referrals/track-click`, { code: referralCode }).catch(() => {});
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin) {
      // DPIA R4: front-end gate matches the server-side gate so users see the
      // age policy *before* hitting the backend. Server still enforces.
      const dob = formData.date_of_birth;
      if (!dob) {
        toast.error("Please enter your date of birth.");
        return;
      }
      const today = new Date();
      const born = new Date(dob);
      const age = today.getFullYear() - born.getFullYear() -
        (today < new Date(today.getFullYear(), born.getMonth(), born.getDate()) ? 1 : 0);
      if (Number.isNaN(age) || born > today) {
        toast.error("That date of birth is invalid.");
        return;
      }
      if (age < 13) {
        toast.error("You must be at least 13 to register. ToyCycleHub is not for children under 13.");
        return;
      }
      if (age < 18 && (!formData.parent_email || !formData.parental_pin)) {
        toast.error("Users under 18 must register with a parent or guardian's email and a parental PIN.");
        return;
      }
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email: formData.email, password: formData.password } : formData;
      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.token, res.data.user);

      // A/B funnel: count successful signups against any active landing-page
      // experiment so we can measure conversion lift. Best-effort; never
      // blocks the redirect.
      if (!isLogin) {
        const anonId = localStorage.getItem("toycycle_anon_id");
        if (anonId) {
          axios.post(`${API}/experiments/track`, {
            key: "landing_hero_cta_v1",
            event: "signup",
            anon_id: anonId,
          }).catch(() => {});
        }
      }

      // Auto-apply the referral code on a fresh signup so we don't make the
      // friend type it manually. Silent on failure — the user can still
      // apply it manually from /referrals.
      if (!isLogin && referralCode) {
        try {
          await axios.post(
            `${API}/referrals/redeem`,
            { code: referralCode },
            { headers: { Authorization: `Bearer ${res.data.token}` } },
          );
          toast.success(`Referral code ${referralCode} applied!`);
        } catch (refErr) {
          logError("Auto-apply referral", refErr);
        }
      }

      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Authentication failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="auth-page">
      <div className="brutalist-card p-8 w-full max-w-md">
        <h1 className="text-3xl font-black uppercase mb-2 text-center" style={{ fontFamily: 'Outfit' }}>{isLogin ? "Welcome Back!" : "Join ToyCycle Hub"}</h1>
        <p className="text-center text-gray-600 mb-6">{isLogin ? "Sign in to your account" : "Create your account to start trading toys"}</p>

        {!isLogin && referralCode && (
          <div
            className="mb-5 border-2 border-gray-900 rounded-xl bg-[#FFF6C7] p-3 flex items-center gap-3"
            data-testid="auth-referral-banner"
          >
            <Gift className="w-5 h-5 flex-shrink-0 text-purple-700" />
            <p className="text-sm">
              <strong>£1 wallet credit</strong> waiting for you — code{" "}
              <span className="font-mono font-bold tracking-wider">{referralCode}</span>{" "}
              will apply automatically when you sign up.
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-wide font-bold mb-2">Your Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="brutalist-input" required placeholder="What should we call you?" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide font-bold mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  className="brutalist-input"
                  required
                  data-testid="auth-dob-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You must be at least 13. Users under 18 need a parent or guardian's email and PIN below.
                </p>
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={formData.is_parent} onChange={(e) => setFormData({...formData, is_parent: e.target.checked})} className="w-5 h-5" />
                  <div>
                    <span className="font-bold">I am a parent/guardian</span>
                    <p className="text-xs text-gray-500">Enable parental controls for this account</p>
                  </div>
                </label>
              </div>
              {formData.is_parent && (
                <div>
                  <label className="block text-xs uppercase tracking-wide font-bold mb-2">Set 4-Digit PIN</label>
                  <input type="password" maxLength={4} value={formData.parental_pin} onChange={(e) => setFormData({...formData, parental_pin: e.target.value.replace(/\D/g, '')})} className="brutalist-input" placeholder="••••" />
                  <p className="text-xs text-gray-500 mt-1">This PIN will be required to approve transactions</p>
                </div>
              )}
              {(() => {
                /* Under-18 sub-form: show parent_email + PIN even when is_parent is unchecked. */
                if (!formData.date_of_birth) return null;
                const born = new Date(formData.date_of_birth);
                const today = new Date();
                const age = today.getFullYear() - born.getFullYear() -
                  (today < new Date(today.getFullYear(), born.getMonth(), born.getDate()) ? 1 : 0);
                if (Number.isNaN(age) || age >= 18 || age < 13) return null;
                return (
                  <div className="border-2 border-yellow-400 bg-yellow-50 rounded-lg p-4 space-y-3" data-testid="auth-minor-block">
                    <div className="flex items-center gap-2 text-yellow-800 font-bold text-sm">
                      <Shield className="w-4 h-4" /> Under-18 account — parental consent required
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wide font-bold mb-1">Parent or guardian's email</label>
                      <input
                        type="email"
                        value={formData.parent_email}
                        onChange={(e) => setFormData({...formData, parent_email: e.target.value})}
                        className="brutalist-input"
                        required
                        data-testid="auth-parent-email"
                      />
                    </div>
                    {!formData.is_parent && !formData.parental_pin && (
                      <div>
                        <label className="block text-xs uppercase tracking-wide font-bold mb-1">Parental PIN (4 digits)</label>
                        <input
                          type="password"
                          maxLength={4}
                          value={formData.parental_pin}
                          onChange={(e) => setFormData({...formData, parental_pin: e.target.value.replace(/\D/g, '')})}
                          className="brutalist-input"
                          placeholder="••••"
                          required
                          data-testid="auth-parental-pin"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Ask your parent to set this. Required for purchases.</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          <div>
            <label className="block text-xs uppercase tracking-wide font-bold mb-2">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="brutalist-input" required />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide font-bold mb-2">Password</label>
            <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="brutalist-input" required minLength={6} />
          </div>
          <button type="submit" disabled={loading} className="brutalist-btn-primary w-full">{getButtonLabel(loading, isLogin)}</button>
        </form>
        <div className="mt-6 text-center">
          <button onClick={() => setIsLogin(!isLogin)} className="text-[#3B82F6] font-bold hover:underline">{isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}</button>
        </div>
      </div>
    </div>
  );
};
